import {
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnInit,
    Output,
    ViewChild,
} from '@angular/core';
import { FormControl, ValidatorFn, Validators } from '@angular/forms';
import { SeErrorStateMatcher } from '../../../utils/error-state-matcher';

export interface NumericValues {
    strModel: string | null;
    numModel: number | null;
    separator: string | null;
}

/**
 * Base Editor Custom AgGrid for Numeric Input.
 * It contains common functionalities used by NumericCellEditorInlineComponent
 * and NumericCellEditorPopupComponent
 */
@Component({
    template: '',
})
export class NumericCellEditorBaseComponent implements OnInit {
    /**
     * Current value of the cell
     * ngModel setted by NumericCellEditorComponent
     */
    @Input() value: number;

    /**
     * (Optional) it allows to specify the minimum admissible value
     * passed by NumericCellEditorComponent
     */
    @Input() min: number;

    /**
     * (Optional) it allows to specify the maximum admissible value
     * passed by NumericCellEditorComponent
     */
    @Input() max: number;

    /**
     * (Optional) it allows to specify the number of decimals
     * passed by NumericCellEditorComponent
     */
    @Input() decimal: number;

    /**
     *  (Optional) it allows to specify if the filed is mandatory
     */
    @Input() required: boolean;

    /**
     *  (Optional) it allows to specify the char used for decimal separator
     */
    @Input() set separatorInUse(val: string) {
        if (!!val) {
            this._separatorInUse = val;
        }
    }
    get separatorInUse(): string {
        return this._separatorInUse;
    }
    private _separatorInUse = ','; // this.cultureService.getDecimalSeparator();

    /**
     * Event emitted when the reactive form is ready
     */
    @Output() formReady: EventEmitter<FormControl> = new EventEmitter<FormControl>();

    /**
     * ElementRef of the input field
     */
    @ViewChild('numericInput', { static: false, read: ElementRef }) numericInput: ElementRef;

    formControl: FormControl;
    matcher = new SeErrorStateMatcher();
    step: string;

    numericFormatStr: string;
    readonly PATTERNS = {
        MONEY: '^([+\\-])?(\\d+)?([.,])?(\\d{1,2})?$',
        NUMERIC_DECIMAL: '^([+\\-])?(\\d+)?([\\.,])?(\\d+)?$',
        NUMERIC_INTEGER: '^[+\\-]?\\d+$',
    };
    pattern = '^([+\\-])?(\\d+)?([\\.,])?(\\d+)?$';

    model: any = { numModel: 0, strModel: '', separator: ',' };

    ngOnInit(): void {
        const validators: Array<ValidatorFn> = [];
        this.model = this.getInternalValue();

        this.formControl = new FormControl(this.model, []);

        if (this.max) {
            validators.push(Validators.max(this.max));
        }
        if (this.min !== null && this.min !== undefined) {
            validators.push(Validators.min(this.min));
        }
        if (this.required) {
            validators.push(Validators.required);
        }

        this.formControl.setValidators(validators);
    }

    private getInternalValue(): string {
        const initialValue: number = this.value;
        return initialValue ? this.makeStringModel(initialValue, this.separatorInUse) : null;
    }

    private makeStringModel(numModel: number, separator: string): string {
        return new Intl.NumberFormat(undefined, {
            minimumFractionDigits: this.decimal ?? 0,
            maximumFractionDigits: this.decimal ?? 10,
            useGrouping: false,
        })
            .format(numModel)
            .replace(/[.,]/, separator);
    }
}
